import {
  DEFAULT_STATE,
  EXPIRY_DAYS,
  QUIZ_STATE_CHANGE,
  QUIZ_VERSION,
  STORAGE_KEY,
  type QuizPersistentState,
} from "../constants";
import type {
  ExplainerCondition,
  ExplainerEntry,
  QuestionHistoryEntry,
  QuizQuestionData,
  QuizResult,
  QuizState,
  QuizView,
} from "../types";
import quizData from "@/lib/data/quiz.json";
import resultData from "@/lib/data/result.json";

interface ResultEntry {
  questiondId: string;
  option?: number;
  result: QuizResult;
}

const FIRST_QUESTION_ID = quizData.questions[0]?.id ?? "";
const questions = quizData.questions as QuizQuestionData[];

function isExpired(completedAt: number | null): boolean {
  if (!completedAt) return false;
  const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - completedAt > expiryMs;
}

function triggerQuizStateChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(QUIZ_STATE_CHANGE));
  }
}

/**
 * Reads quiz state from localStorage. Automatically resets to default
 * if the stored quizVersion doesn't match QUIZ_VERSION or if a completed
 * quiz has expired past EXPIRY_DAYS.
 */
export function getQuizState(): QuizPersistentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };

    const parsed = JSON.parse(raw) as QuizPersistentState;

    if (parsed.quizVersion !== QUIZ_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return { ...DEFAULT_STATE };
    }

    if (parsed.status === 2 && isExpired(parsed.completedAt)) {
      localStorage.removeItem(STORAGE_KEY);
      return { ...DEFAULT_STATE };
    }

    return parsed;
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function setQuizState(partial: Partial<QuizPersistentState>): void {
  try {
    const current = getQuizState();
    const updated = { ...current, ...partial, quizVersion: QUIZ_VERSION };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    triggerQuizStateChange();
  } catch {
    console.error("Failed to set quiz state");
  }
}

export function resetQuizState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    triggerQuizStateChange();
  } catch {
    console.error("Failed to reset quiz state");
  }
}

/**
 * Matches quiz answers against result.json to find the personalized result.
 *
 * Matching logic (mirrors the old quiz-machine getResult):
 *   - Iterates through result entries in order
 *   - For each entry with a questiondId + option, checks if that option
 *     was selected in the user's answers
 *   - First match wins
 *   - The "default" entry acts as fallback (must be last in the array)
 */
export function getQuizResult(
  answers: Record<string, number[]>
): QuizResult | null {
  const entries = resultData as ResultEntry[];

  for (const entry of entries) {
    if (entry.questiondId === "default") {
      return entry.result;
    }

    if (
      entry.questiondId &&
      entry.option !== undefined &&
      answers[entry.questiondId]?.includes(entry.option)
    ) {
      return entry.result;
    }
  }

  return null;
}

export function getMatchingExplainers(
  entries: ExplainerEntry[],
  selectedOptionIds: number[]
): string[] {
  let defaultEntry: ExplainerEntry | undefined;

  for (const entry of entries) {
    const cond = entry.condition as ExplainerCondition;
    if (cond.type === "default") {
      defaultEntry = entry;
      continue;
    }
    if (cond.type === "single" && selectedOptionIds.includes(cond.option)) {
      return entry.explainers;
    }
    if (
      cond.type === "multiple" &&
      cond.options.every((o) => selectedOptionIds.includes(o))
    ) {
      return entry.explainers;
    }
  }

  return defaultEntry?.explainers ?? [];
}

export function getNextQuestion(questionId: string, selectedOptionIds: number[]) {
  const currentQuestion = questions.find((q) => q.id === questionId);
  if (!currentQuestion) return { currentQuestion: undefined, nextQuestion: undefined, nextQuestionId: undefined };

  const firstSelectedOption = currentQuestion.options.find((o) =>
    selectedOptionIds.includes(o.id)
  );

  const nextQuestionId = firstSelectedOption?.next_question_id;
  const nextQuestion = nextQuestionId
    ? questions.find((q) => q.id === nextQuestionId)
    : undefined;

  return { currentQuestion, nextQuestion, nextQuestionId };
}

export function getProgress(state: QuizState): number {
  const currentQuestion = questions.find(
    (q) => q.id === state.currentQuestionId
  );
  const total = questions.length;
  const progress = currentQuestion?.progress ?? 0;
  return Math.round((progress / total) * 100);
}

export function getQuizRestoredState() {
  try {
    const persisted = getQuizState();
    const currentQuestionId =
      questions[persisted.lastQuestion]?.id ?? FIRST_QUESTION_ID;

    const view = (persisted.lastSlideType ?? "question") as QuizView;
    const savedOptions =
      persisted.questionHistory?.find((h) => h.questionId === currentQuestionId)
        ?.selectedOptionIds ?? [];

    let postExplainerView: "question" | "fetchingResult" = "question";
    const lastAnswered =
      persisted.questionHistory?.[persisted.questionHistory.length - 1];
    if (view === "explainer" && lastAnswered) {
      const { nextQuestion: nq } = getNextQuestion(
        lastAnswered.questionId,
        lastAnswered.selectedOptionIds,
      );
      postExplainerView = nq ? "question" : "fetchingResult";
    } else {
      const { nextQuestion } = getNextQuestion(currentQuestionId, savedOptions);
      postExplainerView = nextQuestion ? "question" : "fetchingResult";
    }

    return {
      view,
      currentQuestionId,
      currentExplainerIndex: persisted.lastExplainer ?? 0,
      currentExplainers: persisted.explainersHistory ?? [],
      questionHistory: persisted.questionHistory ?? [],
      selectedOptions: savedOptions,
      postExplainerView,
      isFreshStart: persisted.status === 1 && persisted?.questionHistory?.length === 0,
      config: persisted.config ?? {},
      results: persisted.results ?? undefined,
    };
  } catch {
    return {
      view: "question" as QuizView,
      currentQuestionId: FIRST_QUESTION_ID,
      currentExplainerIndex: 0,
      currentExplainers: [] as string[],
      questionHistory: [] as QuestionHistoryEntry[],
      selectedOptions: [] as number[],
      postExplainerView: "question" as const,
      isFreshStart: true,
      config: {},
      results: undefined,
    };
  }
}

function getResultPageType(results?: Record<string, unknown>): string {
  const title = (results as { title?: string } | undefined)?.title;
  if (!title) return "ng_only";
  return title.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export { FIRST_QUESTION_ID, questions, getResultPageType };
